import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useUnsavedChangesGuard from '../../../hooks/useUnsavedChangesGuard';
import ConfirmModal from '../../utils/ConfirmModal';
import notify from '../../../utils/notify';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { studioLabel } from './adminImportApi';
import { mapWithConcurrency } from './productImport';
import { CONTEXT_TYPES, recordImportApi } from './recordImportApi';
import {
  NEW_CLIENT,
  assignRecordField,
  autoMapRecordColumns,
  buildExistingIndex,
  buildRecordRows,
  chunkRecords,
  existingReason,
  isBilledBefore,
  newClientKey,
  newClientPayload,
  recordLabel,
  recordPayload,
  summarizeRecordRows,
} from './recordImport';
import {
  buildProposalRows,
  detectSellMode,
  ivyProposalEntries,
  ivyProposalSource,
  sheetProposalEntries,
} from './proposalImport';
import { RECORD_TYPES } from './recordTypes';
import { ImportHeader, ImportStepper, ImportTarget } from './ImportFrame';
import RecordMapStep from './RecordMapStep';
import RecordReviewStep from './RecordReviewStep';
import RecordResultStep from './RecordResultStep';
import ImportProgress, { createProgress } from './ImportProgress';
import '../../../styles/ProductImport.css';

// Where the studio finds each kind of record once it's imported.
const WHERE = {
  customers: 'in Planner > Contacts and in the client list when starting a project',
  vendors: 'under Vendors',
  projects: 'in Projects',
  taxLocations: 'in Settings > Tax Locations',
  rooms: 'as rooms on each project',
  proposals: "in each project's Proposals",
  timeEntries: "in each project's Time Billing",
};

const STATUS_LABELS = {
  imported: 'Imported',
  failed: 'Failed',
  skipped: 'Skipped',
  removed: 'Removed (import undone)',
};

const UNDO_BATCH = 500;
const count = (n) => n.toLocaleString('en-US');

const newRunId = () => `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Maps, reviews and saves one sheet of clients, vendors, projects, tax rates,
// rooms, proposals or time entries into the studio a support admin picked.
// `workbook` is every sheet in the file; Ivy's proposals are read from two.
export default function RecordImportWizard({
  account,
  onChangeAccount,
  type,
  fileName,
  sheet,
  workbook = [],
  source,
  onBackToUpload,
  onImportAnother,
  onImportNextSheet,
  nextSheetLabel,
  onFinished,
}) {
  const { noun, nouns, label: Nouns } = RECORD_TYPES[type];
  const studioId = account.userId;
  const studioName = studioLabel(account);
  const api = useMemo(() => recordImportApi(studioId), [studioId]);
  const sourceName = source === 'ivy' ? 'Ivy' : 'the old system';
  const billedLabel = source === 'ivy' ? 'Billed in Ivy' : 'Billed before import';

  const [step, setStep] = useState(2);
  const [mapping, setMapping] = useState(() => autoMapRecordColumns(type, sheet.headers));
  // Ivy's proposals need no mapping: they're read from its Invoices and
  // Invoice Items sections as they are.
  const ivyProposals = useMemo(() => {
    const ivy = type === 'proposals' && source === 'ivy' ? ivyProposalSource(workbook) : null;
    return ivy ? ivyProposalEntries(ivy) : null;
  }, [type, source, workbook]);
  // How a sheet of proposal lines is read: whether its unit selling price is
  // before markup, and how lines without a proposal number are grouped.
  const [proposalOptions, setProposalOptions] = useState(() => ({
    sellMode: (type === 'proposals' && detectSellMode(sheet, mapping)) || 'withMarkup',
    groupBy: 'project',
  }));
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState('');
  const [rows, setRows] = useState([]);
  const [rowStats, setRowStats] = useState(null);
  const [rowsKey, setRowsKey] = useState('');
  const [billed, setBilled] = useState({ mode: 'all', before: '' });
  const [importing, setImporting] = useState(null);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState([]);
  // Clients a projects run added because their client wasn't in the studio.
  const [addedClients, setAddedClients] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  // One run per pass through the wizard. Retries reuse it, so the server knows
  // a row an earlier attempt saved is this run's, and undo only deletes rows
  // this run created.
  const [runId] = useState(newRunId);

  /* ----- Leaving mid-import ----- */
  const dirtyRef = useRef(true);
  const stepRef = useRef(step);
  stepRef.current = step;
  const importingRef = useRef(false);
  const hasUnsavedChanges = useCallback(
    () => importingRef.current || (dirtyRef.current && [2, 3].includes(stepRef.current)),
    []
  );
  const unsavedGuard = useUnsavedChangesGuard(hasUnsavedChanges);

  const changeStudio = () => {
    if (hasUnsavedChanges()) setConfirmSwitch(true);
    else onChangeAccount();
  };

  /* ----- What the studio already has ----- */
  const loadContext = useCallback(async () => {
    setContextError('');
    try {
      const types = CONTEXT_TYPES[type];
      const lists = await Promise.all(types.map((t) => api.load(t)));
      const next = Object.fromEntries(
        types.map((t, i) => [t, Array.isArray(lists[i]) ? lists[i] : []])
      );
      setContext(next);
      return next;
    } catch (err) {
      console.error('Failed to load studio records', err);
      setContextError(err?.message || "Couldn't load this studio's records.");
      return null;
    }
  }, [api, type]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  /* ----- Step 2: mapping ----- */
  const continueToReview = () => {
    if (!context) return;
    const key = JSON.stringify([mapping, proposalOptions]);
    if (key !== rowsKey) {
      let built;
      if (type !== 'proposals') built = buildRecordRows(type, { sheet, mapping, context });
      else {
        const read = ivyProposals || sheetProposalEntries(sheet, mapping, proposalOptions);
        built = buildProposalRows(read, context);
      }
      setRows(built.rows);
      setRowStats(built.stats);
      setRowsKey(key);
    }
    dirtyRef.current = true;
    setStep(3);
  };

  /* ----- Step 3: review ----- */
  const updateRow = useCallback((id, patch) => {
    dirtyRef.current = true;
    setRows((prev) =>
      prev.map((row) =>
        row.id !== id
          ? row
          : {
              ...row,
              ...patch,
              fields: patch.fields ? { ...row.fields, ...patch.fields } : row.fields,
            }
      )
    );
  }, []);

  const selectRows = useCallback((ids, selected) => {
    dirtyRef.current = true;
    const chosen = new Set(ids);
    setRows((prev) =>
      prev.map((row) =>
        chosen.has(row.id) && row.selected !== selected ? { ...row, selected } : row
      )
    );
  }, []);

  // Bulk edits from the review step. Rows that already have the value keep
  // their identity, so the table only redraws what changed.
  const applyToRows = useCallback((ids, key, value) => {
    dirtyRef.current = true;
    const chosen = new Set(ids);
    setRows((prev) =>
      prev.map((row) => {
        if (!chosen.has(row.id) || row.fields[key] === value) return row;
        // A new client is made from the project's client name; without one there's nothing to add.
        if (key === 'clientChoice' && value === NEW_CLIENT && !row.extra.clientName) return row;
        return { ...row, fields: { ...row.fields, [key]: value } };
      })
    );
  }, []);

  const labelFor = useCallback((row) => recordLabel(type, row), [type]);

  /* ----- Saving ----- */

  // Sends records in batches the server accepts, three at a time, and returns
  // one result per record in order. A batch that fails as a whole fails each
  // of its records, so Retry can send them again.
  const saveRecords = async (recordType, records, onSaved) => {
    const saved = new Array(records.length);
    let offset = 0;
    const batches = chunkRecords(records).map((batch) => {
      const start = offset;
      offset += batch.length;
      return { batch, start };
    });
    await mapWithConcurrency(batches, 3, async ({ batch, start }) => {
      try {
        const response = await api.save(recordType, runId, batch);
        batch.forEach((_, i) => {
          saved[start + i] = response?.results?.[i] || {
            ok: false,
            error: 'No answer for this row',
          };
        });
      } catch (err) {
        console.error('Failed to save a batch of records', err);
        batch.forEach((_, i) => {
          saved[start + i] = { ok: false, error: err?.message || 'Failed to save' };
        });
      }
      onSaved?.(batch.length);
    });
    return saved;
  };

  const outcomeOf = (row, status, error = '', recordId = '') => ({
    id: row.id,
    sourceRows: row.sourceRows,
    label: recordLabel(type, row),
    status,
    error,
    recordId,
  });

  // Saves rows and returns one outcome per row, in the order given. Each step
  // reports to the progress window as it goes.
  const importRows = async (rowsToImport) => {
    const isProjects = type === 'projects';
    const steps = createProgress(setProgress, `Importing ${nouns} into ${studioName}`, [
      { key: 'check', label: `Checking what ${studioName} already has` },
      ...(isProjects ? [{ key: 'clients', label: 'Adding new clients' }] : []),
      { key: 'save', label: `Saving ${nouns}` },
    ]);
    steps.start('check');
    const fresh = (await loadContext()) || context;
    const freshIndex = buildExistingIndex(type, fresh);
    const outcomes = new Map();
    const toSave = [];
    rowsToImport.forEach((row) => {
      // What reached the studio since the review was built (the studio adding
      // it, another run) is skipped. Rows re-ticked despite a warning still save.
      const reason = row.existing
        ? ''
        : existingReason(type, row, fresh, { matchImportRef: false, index: freshIndex });
      if (reason) outcomes.set(row.id, outcomeOf(row, 'skipped', reason));
      else toSave.push(row);
    });

    // Sized up front, so the bar only moves forward.
    if (isProjects) {
      steps.plan(
        'clients',
        new Set(toSave.filter((row) => row.fields.clientChoice === NEW_CLIENT).map(newClientKey))
          .size
      );
    }
    steps.plan('save', toSave.length);
    steps.finish('check');

    const total = rowsToImport.length;
    let done = outcomes.size;
    setImporting({ done, total });
    const progress = (n) => {
      done += n;
      setImporting({ done, total });
      steps.tick('save', n);
    };

    // A project whose client isn't in the studio adds the client first, once
    // per client however many projects name it.
    const customers = new Map((fresh.customers || []).map((c) => [c.customerId, c]));
    const newClients = new Map();
    if (type === 'projects') {
      toSave
        .filter((row) => row.fields.clientChoice === NEW_CLIENT)
        .forEach((row) => {
          const key = newClientKey(row);
          if (!newClients.has(key)) newClients.set(key, { payload: newClientPayload(row) });
        });
      const entries = [...newClients.values()];
      steps.start('clients');
      if (entries.length) {
        const saved = await saveRecords(
          'customers',
          entries.map((entry) => entry.payload),
          (n) => steps.tick('clients', n)
        );
        const added = [];
        entries.forEach((entry, i) => {
          if (saved[i].ok) {
            entry.id = saved[i].id;
            if (saved[i].created) added.push(saved[i].id);
          } else {
            entry.error = saved[i].error;
          }
        });
        if (added.length) setAddedClients((prev) => [...new Set([...prev, ...added])]);
      }
      steps.finish('clients');
    }

    const ready = [];
    const payloads = [];
    toSave.forEach((row) => {
      let customer = null;
      if (type === 'projects' && row.fields.clientChoice === NEW_CLIENT) {
        const entry = newClients.get(newClientKey(row));
        if (!entry?.id) {
          outcomes.set(
            row.id,
            outcomeOf(row, 'failed', `Couldn't add its client: ${entry?.error || 'unknown error'}`)
          );
          return;
        }
        customer = { ...entry.payload, customerId: entry.id };
      } else if (type === 'projects' && row.fields.clientChoice) {
        customer = customers.get(row.fields.clientChoice) || null;
      }
      ready.push(row);
      payloads.push(recordPayload(type, row, { customer, billed, billedLabel, context: fresh }));
    });
    done = outcomes.size;
    setImporting({ done, total });

    steps.start('save');
    const saved = await saveRecords(type, payloads, progress);
    steps.finish('save');
    ready.forEach((row, i) => {
      const result = saved[i];
      if (!result.ok) {
        outcomes.set(row.id, outcomeOf(row, 'failed', result.error || `Failed to save ${noun}`));
      } else if (result.created) {
        outcomes.set(row.id, outcomeOf(row, 'imported', '', result.id));
      } else {
        outcomes.set(
          row.id,
          outcomeOf(row, 'skipped', 'Imported into this studio before', result.id)
        );
      }
    });
    return rowsToImport.map((row) => outcomes.get(row.id));
  };

  const reportFinished = (outcomes) =>
    onFinished?.(outcomes.filter((r) => r.status === 'imported').length);

  const runImport = async () => {
    if (importingRef.current) return;
    const selected = rows.filter((row) => row.selected);
    if (!selected.length || summarizeRecordRows(type, rows).blocking > 0) return;

    importingRef.current = true;
    setImporting({ done: 0, total: selected.length });
    try {
      const outcomes = await importRows(selected);
      setResults(outcomes);
      reportFinished(outcomes);
      dirtyRef.current = false;
      setStep(4);
    } catch (err) {
      console.error('Import failed', err);
      notify.error(err?.message || 'The import stopped before finishing. Try again.');
    } finally {
      importingRef.current = false;
      setImporting(null);
      setProgress(null);
    }
  };

  /* ----- Step 4: results ----- */
  const retryFailed = async () => {
    if (importingRef.current) return;
    const failedIds = new Set(results.filter((r) => r.status === 'failed').map((r) => r.id));
    const toRetry = rows.filter((row) => failedIds.has(row.id));
    if (!toRetry.length) return;

    importingRef.current = true;
    setImporting({ done: 0, total: toRetry.length });
    try {
      const outcomes = await importRows(toRetry);
      const byId = new Map(outcomes.map((outcome) => [outcome.id, outcome]));
      const next = results.map((r) => byId.get(r.id) || r);
      setResults(next);
      reportFinished(next);
      const stillFailing = outcomes.filter((o) => o.status === 'failed').length;
      if (stillFailing) {
        notify.warning(
          `${count(stillFailing)} still couldn't be saved. The report has the reasons.`
        );
      } else {
        notify.success(`All ${count(outcomes.length)} retried ${nouns} were imported.`);
      }
    } catch (err) {
      console.error('Retry failed', err);
      notify.error(err?.message || 'The retry stopped before finishing. Try again.');
    } finally {
      importingRef.current = false;
      setImporting(null);
      setProgress(null);
    }
  };

  const undoImport = async () => {
    setConfirmUndo(false);
    const created = results.filter((r) => r.status === 'imported' && r.recordId);
    if (!created.length || importingRef.current) return;
    importingRef.current = true;
    setUndoing(true);
    const removed = new Set();
    const kept = [];
    let stopped = false;
    try {
      const ids = created.map((r) => r.recordId);
      for (let i = 0; i < ids.length; i += UNDO_BATCH) {
        const res = await api.undo(type, runId, ids.slice(i, i + UNDO_BATCH));
        res.deleted.forEach((id) => removed.add(id));
        kept.push(...res.kept);
      }
      // Clients a projects run added go after the projects that use them.
      if (type === 'projects' && addedClients.length) {
        const res = await api.undo('customers', runId, addedClients);
        setAddedClients((prev) => prev.filter((id) => !res.deleted.includes(id)));
      }
    } catch (err) {
      stopped = true;
      console.error('Undo failed', err);
      notify.error(err?.message || 'The undo stopped before finishing. Try again.');
    } finally {
      const next = results.map((r) =>
        r.status === 'imported' && removed.has(r.recordId)
          ? { ...r, status: 'removed', error: '' }
          : r
      );
      setResults(next);
      reportFinished(next);
      importingRef.current = false;
      setUndoing(false);
    }
    if (stopped) return;
    if (removed.size === created.length) {
      notify.success(`Deleted the ${count(removed.size)} ${nouns} this import created.`);
    } else {
      notify.warning(
        `Deleted ${count(removed.size)} of ${count(created.length)}. ${count(kept.length)} ${
          kept.length === 1 ? 'was' : 'were'
        } kept${kept.length ? `: ${kept[0].reason}` : ''}.`
      );
    }
  };

  const downloadReport = () => {
    const base = fileName.replace(/\.[^.]+$/, '') || 'import';
    exportRowsToExcel({
      filename: `${base} - ${nouns} import report.xlsx`,
      sheetName: 'Import report',
      headers: [
        'Excel rows',
        noun.charAt(0).toUpperCase() + noun.slice(1),
        'Status',
        'Problem',
        'InteriorFolio ID',
      ],
      rows: results.map((r) => [
        r.sourceRows.join(', '),
        r.label,
        STATUS_LABELS[r.status] || r.status,
        r.error || '',
        r.status === 'imported' ? r.recordId : '',
      ]),
    });
  };

  /* ----- Layout ----- */
  const importedCount = results.filter((r) => r.status === 'imported').length;
  const resultNotes = [];
  if (type === 'projects' && addedClients.length) {
    resultNotes.push(
      `Also added ${count(addedClients.length)} ${
        addedClients.length === 1 ? 'client' : 'clients'
      } for projects whose client wasn't in the studio.`
    );
  }
  if (type === 'timeEntries' && billed.mode !== 'none') {
    const importedIds = new Set(results.filter((r) => r.status === 'imported').map((r) => r.id));
    const billedCount = rows.filter(
      (r) => importedIds.has(r.id) && isBilledBefore(r, billed)
    ).length;
    if (billedCount) {
      resultNotes.push(
        `${count(billedCount)} ${billedCount === 1 ? 'entry is' : 'entries are'} marked “${billedLabel}”, so ${billedCount === 1 ? "it can't" : "they can't"} be billed again.`
      );
    }
  }
  if (type === 'taxLocations' && importedCount) {
    resultNotes.push(
      'Imported rates have no QuickBooks tax code yet. If the studio uses QuickBooks, map each one in Settings > Tax Locations.'
    );
  }
  if (type === 'proposals') {
    const importedIds = new Set(results.filter((r) => r.status === 'imported').map((r) => r.id));
    const archivedCount = rows.filter(
      (r) => importedIds.has(r.id) && r.fields.status === 'archived'
    ).length;
    if (archivedCount) {
      resultNotes.push(
        `${count(archivedCount)} ${archivedCount === 1 ? 'proposal is' : 'proposals are'} Archived: kept for reference, hidden from the client and owing nothing. Emailing one to the client from the proposal makes it Sent.`
      );
    }
  }

  const resultHeading = () => {
    if (importedCount === 0 && results.some((r) => r.status === 'removed')) {
      return ['Import undone', `The ${nouns} from this import were deleted from ${studioName}.`];
    }
    if (results.some((r) => r.status === 'failed')) {
      return ['Import complete', `Some ${nouns} couldn't be imported into ${studioName}.`];
    }
    return ['Import complete', `The ${nouns} have been imported into ${studioName}.`];
  };
  const headings = {
    2: ivyProposals
      ? ['Proposals in This File', "Ivy's proposals are read as they are, with nothing to map."]
      : ['Map Columns', 'Match your Excel columns to InteriorFolio fields.'],
    3: [`Review ${Nouns}`, 'Check imported details and make corrections before saving.'],
    4: step === 4 ? resultHeading() : ['', ''],
  };
  const [title, subtitle] = headings[step];

  const undoExtras = [];
  if (type === 'customers' || type === 'vendors')
    undoExtras.push('the Planner contacts made for them');
  if (type === 'projects' && addedClients.length) {
    undoExtras.push(
      `the ${count(addedClients.length)} ${addedClients.length === 1 ? 'client' : 'clients'} added for them`
    );
  }

  return (
    <div className="pi-page">
      <ImportTarget
        account={account}
        onChangeStudio={changeStudio}
        disabled={Boolean(importing) || undoing}
      />
      <ImportHeader title={title} subtitle={subtitle} />
      <ImportStepper
        labels={['Upload file', 'Map Columns', `Review ${Nouns}`, 'Import']}
        step={step}
      />

      <div className={`pi-body pi-body--step${step}`}>
        {step === 2 && (
          <RecordMapStep
            type={type}
            fileName={fileName}
            sheet={sheet}
            mapping={mapping}
            onChangeField={(col, field) => {
              dirtyRef.current = true;
              setMapping((prev) => assignRecordField(prev, col, field));
            }}
            ivyProposals={ivyProposals}
            proposalOptions={proposalOptions}
            onChangeProposalOptions={(patch) => {
              dirtyRef.current = true;
              setProposalOptions((prev) => ({ ...prev, ...patch }));
            }}
            contextLoading={!context && !contextError}
            contextError={contextError}
            onRetryContext={loadContext}
            onBack={onBackToUpload}
            onContinue={continueToReview}
          />
        )}

        {step === 3 && rowStats && (
          <RecordReviewStep
            type={type}
            noun={noun}
            nouns={nouns}
            fileName={fileName}
            sheetName={sheet.section ? sheet.name : ''}
            rows={rows}
            rowStats={rowStats}
            context={context}
            labelFor={labelFor}
            onUpdateRow={updateRow}
            onSelectRows={selectRows}
            onApplyToRows={applyToRows}
            billed={billed}
            onChangeBilled={setBilled}
            billedLabel={billedLabel}
            sourceName={sourceName}
            onBack={() => setStep(2)}
            onImport={runImport}
            importing={importing}
          />
        )}

        {step === 4 && (
          <RecordResultStep
            noun={noun}
            nouns={nouns}
            where={WHERE[type]}
            fileName={fileName}
            studioName={studioName}
            results={results}
            notes={resultNotes}
            onRetryFailed={retryFailed}
            retrying={importing}
            onUndo={() => setConfirmUndo(true)}
            undoing={undoing}
            onDownloadReport={downloadReport}
            onImportAnother={onImportAnother}
            onImportNextSheet={onImportNextSheet}
            nextSheetLabel={nextSheetLabel}
            onChangeStudio={onChangeAccount}
          />
        )}
      </div>

      <ImportProgress progress={progress} />

      {/* Portaled so they stack above the open mobile sidebar. */}
      {createPortal(
        <>
          <ConfirmModal
            isOpen={unsavedGuard.isPrompting}
            title="Leave this import?"
            message={
              importing || undoing
                ? `${Nouns} are still being saved or deleted. Leaving now stops partway through.`
                : 'Your column choices and edits will be lost.'
            }
            confirmLabel="Leave Import"
            destructive={true}
            onConfirm={unsavedGuard.confirmLeave}
            onCancel={unsavedGuard.cancelLeave}
          />
          <ConfirmModal
            isOpen={confirmSwitch}
            title="Switch studios?"
            message={`Your column choices and edits for ${studioName} will be lost.`}
            confirmLabel="Switch studio"
            destructive={true}
            onConfirm={() => {
              setConfirmSwitch(false);
              onChangeAccount();
            }}
            onCancel={() => setConfirmSwitch(false)}
          />
          <ConfirmModal
            isOpen={confirmUndo}
            title="Undo this import?"
            message={`This deletes the ${count(importedCount)} ${
              importedCount === 1 ? noun : nouns
            } this import created in ${studioName}${
              undoExtras.length ? `, with ${undoExtras.join(' and ')}` : ''
            }.${
              type === 'projects'
                ? ' Anything the studio has added to these projects since stays behind without its project.'
                : ''
            }${
              type === 'proposals'
                ? ' Any sent to a client, paid or invoiced since then are kept.'
                : ''
            }`}
            confirmLabel={`Delete ${count(importedCount)} ${importedCount === 1 ? noun : nouns}`}
            destructive={true}
            onConfirm={undoImport}
            onCancel={() => setConfirmUndo(false)}
          />
        </>,
        document.body
      )}
    </div>
  );
}
